import { useEffect, useRef } from "react";
import { CheckCircle, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMaintenanceContext } from "@/contexts/MaintenanceContext";
import { cn } from "@/lib/utils";

const AUTO_DISMISS_MS = 30_000;

export const PostMaintenanceBanner: React.FC = () => {
  const { showReturnBanner, hasDraft, restoreDraft, dismissBanner } = useMaintenanceContext();

  const restoreButtonRef = useRef<HTMLButtonElement>(null);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Auto-focus "Retomar trabalho" button when banner appears (accessibility)
  useEffect(() => {
    if (showReturnBanner && hasDraft && !prefersReducedMotion) {
      restoreButtonRef.current?.focus();
    }
  }, [showReturnBanner, hasDraft, prefersReducedMotion]);

  // Auto-dismiss after 30s when no draft (AC4)
  useEffect(() => {
    if (!showReturnBanner || hasDraft) return;
    const timer = setTimeout(dismissBanner, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [showReturnBanner, hasDraft, dismissBanner]);

  if (!showReturnBanner) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed top-16 left-0 right-0 z-40 border-b px-4 py-3",
        "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800",
        !prefersReducedMotion && "animate-in slide-in-from-top"
      )}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <span className="text-sm font-medium text-green-800 dark:text-green-200">
            Manutenção concluída. O sistema está operacional.
          </span>
        </div>

        <div className="flex w-full gap-2 sm:w-auto">
          {hasDraft ? (
            <>
              <Button
                ref={restoreButtonRef}
                variant="default"
                size="sm"
                className="w-full sm:w-auto"
                onClick={restoreDraft}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Retomar trabalho
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full sm:w-auto"
                aria-label="Ignorar restauração de rascunho"
                onClick={dismissBanner}
              >
                Ignorar
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full sm:w-auto"
              aria-label="Fechar banner de manutenção concluída"
              onClick={dismissBanner}
            >
              <X className="mr-1.5 h-4 w-4" />
              Fechar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
